import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { TextInputFormField } from '../../../Shared/TextInputFormField';
import CustomerCareDTable from '../../../Shared/CustomerCareDTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { TabProps } from '../FacilityDetails';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  addNote,
  addNoteByLocation,
  deleteNote,
  deleteNoteByLocation,
  editNote,
  editNoteByLocation,
  getNotesList,
  getNotesListByLocation,
} from '../../../../store/facility/actionCreators';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import { parseDateToLocal } from '../../../../shared/utils';
import DeleteNoteConfirmation from '../../../Hubs/HubDetails/tabs/components/DeleteNoteConfirmation';
import UpdateNoteForm from '../../../Hubs/HubDetails/tabs/components/UpdateNoteForm';
import NoteActionFeedbackModel from '../../../Hubs/HubDetails/tabs/components/NoteActionFeedbackModel';
import {
  dimGrey,
  lightBackground1,
  lightBlack,
  lightGrey,
} from '../../../../mainTheme';

const validationSchema = yup.object({
  note: yup.string().required('Note content is required'),
});

const NotesTab: React.FC<TabProps> = ({
  parentFacilityId,
  locationId,
  isFacilityAdmin,
}) => {
  const dispatch = useDispatch();
  const { notesList, isLoading, noteActionStatus } = useSelector(
    (state: RootState) => state.facility,
  );
  const formikRef = useRef<any>(null);
  const [deleteNoteId, setDeleteNoteId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<any>(null);

  useEffect(() => {
    if (isFacilityAdmin && locationId) {
      dispatch(getNotesListByLocation(locationId));
    } else if (parentFacilityId) {
      if (locationId) {
        dispatch(getNotesList(parentFacilityId, locationId));
      } else {
        dispatch(getNotesList(parentFacilityId));
      }
    }
  }, [dispatch, parentFacilityId, locationId, isFacilityAdmin]);

  const handleSubmit = (values: { note: string }) => {
    if (isFacilityAdmin && locationId) {
      // Use this API for admin/location
      dispatch(addNoteByLocation(values.note, locationId, parentFacilityId));
    } else if (parentFacilityId) {
      if (locationId) {
        // Add note against location
        dispatch(addNote(values.note, parentFacilityId, locationId));
      } else {
        // Add note for facility
        dispatch(addNote(values.note, parentFacilityId));
      }
    }
    // Reset form after submission
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  const handleEdit = (noteId: number) => {
    const noteToEdit = notesList?.find((note) => note.note_id === noteId);
    if (noteToEdit) {
      setSelectedNote(noteToEdit);
      setEditModalOpen(true);
    }
  };

  const handleEditSubmit = (values: any) => {
    if (selectedNote) {
      if (isFacilityAdmin && locationId) {
        // Use this API for admin/location
        dispatch(
          editNoteByLocation(locationId, selectedNote.note_id, values.note),
        );
      } else {
        dispatch(editNote(selectedNote.note_id, values.note));
      }
      setEditModalOpen(false);
      setSelectedNote(null);
    }
  };

  const handleDeleteConfirm = (noteId: number) => {
    setDeleteNoteId(noteId);
  };

  const handleDelete = () => {
    if (deleteNoteId) {
      if (isFacilityAdmin && locationId) {
        dispatch(deleteNoteByLocation(locationId, deleteNoteId));
      } else {
        dispatch(deleteNote(deleteNoteId));
      }
      setDeleteNoteId(null);
    }
  };

  const ActionCell = ({ params }: { params: { note_id: number } }) => (
    <Box
      display="flex"
      justifyContent="end"
      borderRadius="8px"
      border="1px solid var(--Outline-O5---Separator, #D4D4D9)"
    >
      <Tooltip title="Edit">
        <IconButton onClick={() => handleEdit(params.note_id)} size="small">
          <FontAwesomeIcon
            icon={faPenToSquare}
            style={{ marginRight: 8 }}
            size="sm"
          />
        </IconButton>
      </Tooltip>
      <Box
        width="1px"
        bgcolor="var(--Outline-O5---Separator, #D4D4D9)"
        marginY={1}
      />
      <Tooltip title="Delete">
        <IconButton
          onClick={() => handleDeleteConfirm(params.note_id)}
          size="small"
        >
          <FontAwesomeIcon
            icon={faTrashCan}
            size="sm"
            style={{ marginLeft: 8 }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const columns = [
    {
      field: 'note',
      headerName: 'Note',
      flex: 2.5,
      sortable: true,
      renderCell: (params: any) => (
        <Box
          sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <Tooltip
            title={
              <Box
                sx={{
                  p: 2,
                  bgcolor: lightBackground1,
                  border: '1px solid #D4D4D9',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                  maxWidth: {
                    xs: '18.75rem',
                    sm: '37.5rem',
                    md: '53.125rem',
                    lg: '62.5rem',
                    xl: '78.125rem',
                  },
                  color: dimGrey,
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0.2px',
                  }}
                >
                  {params.value}
                </Typography>
              </Box>
            }
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: { bgcolor: 'transparent', p: 0, maxWidth: 'none' },
              },
            }}
          >
            <Box
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {params.value}
            </Box>
          </Tooltip>
        </Box>
      ),
    },
    ...(locationId
      ? []
      : [
          {
            field: 'location_name',
            headerName: 'Location',
            flex: 1.5,
            sortable: true,
            renderCell: (params: any) => params.value || 'N/A',
          },
        ]),
    {
      field: 'createdBy',
      headerName: 'Created by',
      flex: 1,
      sortable: true,
      valueGetter: (params: any) => {
        const firstName = params.row.created_by_first_name || '';
        const lastName = params.row.created_by_last_name || '';
        return firstName || lastName ? `${firstName} ${lastName}` : 'N/A';
      },
    },
    {
      field: 'created_at',
      headerName: 'Date added',
      flex: 1,
      sortable: true,
      renderCell: (params: any) =>
        params.value
          ? parseDateToLocal(params.value, 'yyyy-MM-dd HH:mm:ss')
          : 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      sortable: false,
      renderCell: (params: any) => <ActionCell params={params.row} />,
    },
  ];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Formik
        innerRef={formikRef}
        initialValues={{ note: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formik) => (
          <Form>
            <Box>
              <Field
                label=""
                name="note"
                placeholder="Type here to add a new note..."
                component={TextInputFormField}
                multiline
                rows={2}
                variant="standard"
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: '8px',
                    border: '1px solid #D4D4D9',
                    '& textarea': {
                      '&::placeholder': {
                        color: lightGrey,
                      },
                    },
                    '&::before': {
                      display: 'none',
                    },
                    '&::after': {
                      display: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px',
                    color: lightBlack,
                  },
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={!formik.values.note || formik.isSubmitting}
                  sx={{ height: '40px' }}
                >
                  Save note
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>

      {notesList?.length ? (
        <CustomerCareDTable
          rows={notesList}
          columns={columns}
          pageSize={5}
          defaultSortField="created_at"
          defaultSortDirection="desc"
          hideViewAllButton
          idField="note_id"
        />
      ) : null}

      {editModalOpen && selectedNote && (
        <UpdateNoteForm
          initialValues={{
            note: selectedNote.note,
            category: 'Facility',
          }}
          onSubmit={handleEditSubmit}
          noteTypeOptions={[]}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedNote(null);
          }}
          showCategory={false}
        />
      )}

      {deleteNoteId !== null && (
        <DeleteNoteConfirmation
          onClose={() => setDeleteNoteId(null)}
          onDelete={handleDelete}
        />
      )}

      {noteActionStatus.show && (
        <NoteActionFeedbackModel
          onClose={() => {
            if (isFacilityAdmin && locationId) {
              dispatch(getNotesListByLocation(locationId));
            } else if (parentFacilityId) {
              if (locationId) {
                dispatch(getNotesList(parentFacilityId, locationId));
              } else {
                dispatch(getNotesList(parentFacilityId));
              }
            }
          }}
          isFromFacilities
        />
      )}
    </Box>
  );
};

export default NotesTab;
